<template>
  <div class="dashboard-announcements">
    <h4 class="title">
      {{ $t('elanlar') }}
    </h4>

    <!-- Loading state -->
    <div
      v-if="loading"
      class="card announcement-card"
    >
      <div class="card-body loading-state">
        <div class="spinner-container">
          <div class="spinner" />
        </div>
        <p>{{ $t('login.yklnir') }}</p>
      </div>
    </div>

    <!-- Empty state with icon -->
    <div
      v-else-if="!announcements || announcements.length === 0"
      class="card announcement-card"
    >
      <div class="card-body empty-state">
        <div class="empty-icon">
          <i class="fa fa-bullhorn" />
        </div>
        <p>{{ $t('common.noAnnouncements', 'No announcements available') }}</p>
      </div>
    </div>

    <!-- Instagram stories-like slider -->
    <div
      v-else
      class="card announcement-card"
    >
      <div class="card-body">
        <VuePerfectScrollbar class="stories-scroll-area">
          <div class="stories-container">
            <div
              v-for="announcement in announcements"
              :key="announcement.id"
              class="story-item"
              @click="viewAnnouncement(announcement)"
            >
              <div class="story-image-wrapper" :class="{'unread': announcement.isRead < 1}">
                <img
                  :src="getPhoto(announcement.photo, 'thumbnailCropped')"
                  :alt="announcement.title || $t('common.untitled')"
                  class="story-image"
                >
                <div 
                  v-if="announcement.isRead < 1" 
                  class="dotted-animation"
                >
                  <span class="animate-circle" />
                  <span class="main-circle" />
                </div>
              </div>
              <p class="story-title">
                {{ (announcement.title || $t('common.untitled')).substring(0, 15) + (announcement.title && announcement.title.length > 15 ? '...' : '') }}
              </p>
            </div>
          </div>
        </VuePerfectScrollbar>
      </div>
    </div>
  </div>
</template>

<script>
import {getPhoto} from "@/repositories/Utils";
import {mapState} from "vuex";
import {getAnnouncements} from "@/repositories/GeneralDataRepository";
import VuePerfectScrollbar from 'vue-perfect-scrollbar';

export default {
  name: 'DashboardAnnouncements',
  components: {
    VuePerfectScrollbar
  },
  data() {
    return {
      announcements: [],
      loading: true
    };
  },

  computed: {
    ...mapState({
      token: state => state.auth.token,
    })
  },

  mounted() {
    this.fetchAnnouncements();
  },

  methods: {
    /**
     * Fetch announcements from the API
     */
    fetchAnnouncements() {
      this.loading = true;

      getAnnouncements(this.token)
        .then(data => {
          this.announcements = data;
        })
        .catch(error => {
          console.error('Error fetching announcements:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
     * Get photo URL from path and size
     * @param {string} path - Path to the photo
     * @param {string} size - Size of the photo
     * @return {string} - URL of the photo
     */
    getPhoto(path, size) {
      if (!path) return require('@/assets/images/icons/dashboard/vendors.png');
      return getPhoto(path, size);
    },

    /**
     * Get the date from an announcement object
     * @param {Object} announcement - Announcement object
     * @return {string|null} - Date string or null if not found
     */
    getAnnouncementDate(announcement) {
      if (!announcement) return null;

      // Check for common date properties
      return announcement.date || 
             announcement.createdAt || 
             announcement.created || 
             announcement.publishedAt || 
             announcement.updatedAt || 
             null;
    },

    /**
     * Format date to a readable format
     * @param {string} dateString - Date string to format
     * @return {string} - Formatted date
     */
    formatDate(dateString) {
      if (!dateString) return '';

      try {
        const date = new Date(dateString);

        // Check if date is valid
        if (isNaN(date.getTime())) return '';

        return new Intl.DateTimeFormat(this.$i18n.locale || 'en', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        }).format(date);
      } catch (error) {
        console.error('Error formatting date:', error);
        return '';
      }
    },

    /**
     * Navigate to announcement detail page
     * @param {Object} announcement - Announcement object
     */
    viewAnnouncement(announcement) {
      if (!announcement || !announcement.id) return;

      this.$router.push(`/dashboard/announcement/${announcement.id}`);
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/theme/_variables.scss";

.dashboard-announcements {
  .title {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 1.5rem;
    color: darken($theme-font-color, 10%);
    letter-spacing: 0.5px;
  }

  // Card styling
  .announcement-card {
    border: none;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    overflow: hidden;
    margin-bottom: 1.5rem;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
    }
  }

  .card-body {
    padding: 1.25rem;

    // Loading state
    &.loading-state {
      text-align: center;
      padding: 3rem 1.5rem;
      color: $theme-font-color;

      .spinner-container {
        margin-bottom: 1rem;

        .spinner {
          width: 40px;
          height: 40px;
          margin: 0 auto;
          border: 3px solid rgba(#2062FB, 0.2);
          border-top-color: #2062FB;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
      }

      p {
        margin: 0;
        font-size: 16px;
        color: lighten($theme-font-color, 15%);
      }
    }

    // Empty state
    &.empty-state {
      text-align: center;
      padding: 3rem 1.5rem;
      color: $theme-font-color;

      .empty-icon {
        margin-bottom: 1rem;
        font-size: 2.5rem;
        color: lighten($theme-font-color, 40%);
      }

      p {
        margin: 0;
        font-size: 16px;
        color: lighten($theme-font-color, 15%);
      }
    }
  }

  .card-footer {
    background-color: rgba(#f8f9fa, 0.5);
    border-top: 1px solid rgba(0, 0, 0, 0.05);
    padding: 0.75rem 1.25rem;
    text-align: center;

    .view-all-btn {
      font-size: 0.875rem;
      font-weight: 500;
      padding: 0.375rem 1rem;
      border-radius: 50px;
      transition: all 0.2s ease;

      &:hover {
        transform: translateX(3px);
      }

      i {
        transition: transform 0.2s ease;
      }

      &:hover i {
        transform: translateX(3px);
      }
    }
  }

  // Instagram stories-like slider
  .stories-scroll-area {
    position: relative;
    max-width: 100%;
    overflow: hidden;

    .ps {
      overflow-x: auto;
      overflow-y: hidden;
    }
  }

  .stories-container {
    display: flex;
    white-space: nowrap;
    padding: 0.5rem 0;
  }

  .story-item {
    display: inline-block;
    margin-right: 1.25rem;
    text-align: center;
    cursor: pointer;
    transition: transform 0.3s ease;
    width: 80px;

    &:last-child {
      margin-right: 0;
    }

    &:hover {
      transform: scale(1.05);
    }

    .story-image-wrapper {
      position: relative;
      width: 80px;
      height: 80px;
      margin: 0 auto;
      border-radius: 50%;
      padding: 3px;
      background: linear-gradient(45deg, #f3f3f3, #e6e6e6);

      &.unread {
        background: linear-gradient(45deg, #2062FB, #28a745);
      }

      .story-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 50%;
        border: 2px solid white;
      }
    }

    .story-title {
      margin-top: 0.5rem;
      font-size: 0.75rem;
      color: $theme-font-color;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 80px;
    }
  }

  // Dotted animation for unread stories
  .dotted-animation {
    position: absolute;
    top: -5px;
    right: -5px;

    .animate-circle {
      position: absolute;
      top: 7px;
      right: 7px;
      height: 15px;
      width: 15px;
      border: 3px solid #28a745;
      border-radius: 70px;
      animation: animate 1s linear infinite;
    }

    .main-circle {
      position: absolute;
      top: 10px;
      right: 10px;
      height: 9px;
      width: 9px;
      background-color: #28a745;
      border-radius: 50%;
      z-index: 1;
    }
  }

  // Animations
  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  @keyframes animate {
    0% {
      transform: scale(0.5);
      opacity: 0.0;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      transform: scale(1.2);
      opacity: 0.0;
    }
  }

  // Responsive design
  @media (max-width: 768px) {
    .title {
      font-size: 18px;
      margin-bottom: 1rem;
    }

    .story-item {
      width: 70px;
      margin-right: 1rem;

      .story-image-wrapper {
        width: 70px;
        height: 70px;
      }

      .story-title {
        font-size: 0.7rem;
        max-width: 70px;
      }
    }
  }

  @media (max-width: 576px) {
    .card-body {
      padding: 1rem;
    }

    .story-item {
      width: 60px;
      margin-right: 0.75rem;

      .story-image-wrapper {
        width: 60px;
        height: 60px;
      }

      .story-title {
        font-size: 0.65rem;
        max-width: 60px;
      }
    }
  }
}
</style>
